import type { Appointment, BlockedTimeEvent, Booking } from '../types/appointment.types';
import { APPOINTMENT_TYPES, type AppointmentType } from '../constants/appointmentData';
import { getLocalDateString } from '@/lib/utils/timeUtils';
import { addDays, isSameDay, startOfDay } from 'date-fns';

/**
 * Convert API data (blocked times + bookings) to unified Appointment format
 */
export function convertToAppointments(blockedTimes: BlockedTimeEvent[], bookings: Booking[]): Appointment[] {
    const appointments: Appointment[] = [];
    const seenBlockedTimeIds = new Set<string>(); // Track blocked time IDs to avoid duplicates

    // Helper function: Format times as HH:MM in 24-hour format using explicit hour/minute extraction
    // This is more reliable than toLocaleTimeString across different browsers and timezones
    const formatTime24 = (date: Date) => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // First, add all bookings (like Vue does)
    bookings.forEach(booking => {
        const startDate = new Date(booking.start_utc);
        const endDate = new Date(booking.end_utc);

        appointments.push({
            id: booking.booking_id,
            title: booking.title || 'Appointment',
            date: getLocalDateString(startDate),
            startTime: formatTime24(startDate),
            endTime: formatTime24(endDate),
            type: APPOINTMENT_TYPES.BOOKING,
            status: booking.booking_status === 0 ? 'canceled' : 'booked',
            attendees: booking.attendees?.length || 0,
            attendeesList:
                booking.attendees?.map(att => ({
                    name: att.name,
                    email: att.email,
                    status: att.status,
                })) || [],
            // Only store cal_ref_id if it's a valid value (not 'None' or empty)
            calRefId:
                booking.booking_cal_ref_id && booking.booking_cal_ref_id !== 'None'
                    ? booking.booking_cal_ref_id
                    : undefined,
        });
    });

    // Then, add blocked times (checking for duplicates per date, like Vue does)
    blockedTimes.forEach(blockedTime => {
        // Skip if we've already processed this blocked time ID
        if (seenBlockedTimeIds.has(blockedTime.id)) return;
        seenBlockedTimeIds.add(blockedTime.id);

        const startDate = new Date(blockedTime.start_utc);
        const endDate = new Date(blockedTime.end_utc);

        // Skip invalid time blocks where end is before or equal to start (corrupted data)
        if (endDate <= startDate) {
            console.warn('Skipping invalid blocked time with end <= start:', blockedTime);
            return;
        }

        // Check if the block spans multiple calendar days in local timezone
        // Vue's logic: Calculate duration in days, if it's exactly 1 day (24h), don't split
        const durationMs = endDate.getTime() - startDate.getTime();
        const durationDays = Math.ceil(durationMs / (24 * 60 * 60 * 1000));
        const startDateString = getLocalDateString(startDate);
        const endDateString = getLocalDateString(endDate);
        const isMultiDay = startDateString !== endDateString && durationDays > 1;

        // Determine appointment type
        const isGcalBlock = blockedTime.type === 'Google Meet';
        const appointmentType: AppointmentType = isGcalBlock
            ? APPOINTMENT_TYPES.GOOGLE_MEET
            : APPOINTMENT_TYPES.BLOCKED;

        // Helper function to add a blocked time for a specific date if not duplicate
        const addBlockedTimeForDate = (date: string, startTime: string, endTime: string) => {
            // Vue's logic: Skip blocked time if a BOOKING with the same cal_ref_id already exists on this date
            // This prevents duplicating bookings that are synced to Google Calendar
            if (blockedTime.cal_ref_id && blockedTime.cal_ref_id !== 'None') {
                const hasDuplicateBooking = appointments.some(
                    apt =>
                        apt.date === date &&
                        apt.type === APPOINTMENT_TYPES.BOOKING &&
                        apt.calRefId === blockedTime.cal_ref_id
                );
                if (hasDuplicateBooking) {
                    return; // Skip - this Google Calendar event is already represented as a booking
                }
            }

            // For manual blocks with null cal_ref_id, deduplicate by exact time slot
            // This prevents showing duplicate blocks when the API returns duplicates
            if (!blockedTime.cal_ref_id || blockedTime.cal_ref_id === 'None') {
                const hasExactTimeSlotDuplicate = appointments.some(
                    apt =>
                        apt.date === date &&
                        apt.startTime === startTime &&
                        apt.endTime === endTime &&
                        apt.title === (blockedTime.name || 'Blocked Time') &&
                        !apt.calRefId // Only compare with other blocks that have no cal_ref_id
                );
                if (hasExactTimeSlotDuplicate) {
                    return; // Skip - exact duplicate time slot
                }
            }

            appointments.push({
                id: blockedTime.id,
                title: blockedTime.name || 'Blocked Time',
                date,
                startTime,
                endTime,
                type: appointmentType,
                status: 'booked',
                attendees: 0,
                calRefId:
                    blockedTime.cal_ref_id && blockedTime.cal_ref_id !== 'None' ? blockedTime.cal_ref_id : undefined,
            });
        };

        if (!isMultiDay) {
            // Single day block
            addBlockedTimeForDate(startDateString, formatTime24(startDate), formatTime24(endDate));
        } else {
            // Multi-day block - split into separate days
            const startDay = startOfDay(startDate);
            const endDay = startOfDay(endDate);

            // If end time is exactly at midnight (00:00:00), don't include that day
            // This matches Vue's Math.ceil logic which doesn't count partial days
            const endHours = endDate.getHours();
            const endMinutes = endDate.getMinutes();
            const endSeconds = endDate.getSeconds();
            const isEndExactlyMidnight = endHours === 0 && endMinutes === 0 && endSeconds === 0;
            const lastDayToInclude = isEndExactlyMidnight ? addDays(endDay, -1) : endDay;

            let currentDay = startDay;
            while (currentDay <= lastDayToInclude) {
                const isFirstDay = isSameDay(currentDay, startDay);
                const isLastDay = isSameDay(currentDay, lastDayToInclude);

                const dayStartTime = isFirstDay ? formatTime24(startDate) : '00:00';

                const dayEndTime = isLastDay ? formatTime24(endDate) : '23:59';

                addBlockedTimeForDate(getLocalDateString(currentDay), dayStartTime, dayEndTime);

                currentDay = addDays(currentDay, 1);
            }
        }
    });

    return appointments;
}

/**
 * Group appointments by date for calendar aggregation
 */
export function groupAppointmentsByDate(
    appointments: Appointment[]
): Record<string, { blockedTimes: number; appointments: number }> {
    const grouped: Record<string, { blockedTimes: number; appointments: number }> = {};

    // First, identify booking time slots to avoid counting duplicate Google Calendar events
    const bookingTimeSlots = new Set<string>();
    appointments.forEach(apt => {
        if (apt.type === APPOINTMENT_TYPES.BOOKING && apt.status !== 'canceled') {
            // Create a unique key for this time slot
            bookingTimeSlots.add(`${apt.date}-${apt.startTime}-${apt.endTime}`);
        }
    });

    appointments.forEach(apt => {
        const dateKey = apt.date;
        if (!dateKey) return;

        if (!grouped[dateKey]) {
            grouped[dateKey] = {
                blockedTimes: 0,
                appointments: 0,
            };
        }

        if (apt.type === APPOINTMENT_TYPES.BLOCKED) {
            grouped[dateKey].blockedTimes += 1;
        } else if (apt.type === APPOINTMENT_TYPES.GOOGLE_MEET) {
            // Check if this Google Calendar event conflicts with an appointment
            const timeSlotKey = `${apt.date}-${apt.startTime}-${apt.endTime}`;
            const isDuplicate = bookingTimeSlots.has(timeSlotKey);

            // Only count if it's not a duplicate of an appointment
            if (!isDuplicate) {
                grouped[dateKey].blockedTimes += 1;
            }
        } else if (apt.type === APPOINTMENT_TYPES.BOOKING && apt.status !== 'canceled') {
            // Only count non-canceled appointments for calendar view
            grouped[dateKey].appointments += 1;
        }
    });

    return grouped;
}
